/**
 * Guest logs action functions
 */
import * as types from "../actionTypes";
import * as guestLogsAPI from "../api/guestLogsAPI";
import {setApiError, setApiStart, setApiSuccess} from "./settingsActions";
import qs from "qs";

/**
 * Set loading state for guest logs
 * @param {boolean} loading - Loading state
 * @returns {Object} - Action object
 */
export const setGuestLogsLoading = (loading) => {
  return {type: types.SET_GUEST_LOGS_LOADING, loading};
};

/**
 * Set guest logs in the store
 * @param {Array} logs - Guest logs to set
 * @param {boolean} hasMore - Whether there are more logs to fetch
 * @param {Object} lastItem - Last item object for pagination
 * @returns {Object} - Action object
 */
export const setGuestLogs = (logs, hasMore, lastItem) => {
  return {type: types.SET_GUEST_LOGS, logs, hasMore, lastItem};
};

/**
 * Add guest logs to the store (for pagination)
 * @param {Array} logs - Guest logs to add
 * @param {boolean} hasMore - Whether there are more logs to fetch
 * @param {Object} lastItem - Last item object for pagination
 * @returns {Object} - Action object
 */
export const addGuestLogs = (logs, hasMore, lastItem) => {
  return {type: types.ADD_GUEST_LOGS, logs, hasMore, lastItem};
};

/**
 * Fetch guest logs from the API
 * @param {Object} params - Request parameters
 * @param {string} params.guestId - Guest ID
 * @param {boolean} [params.append=false] - Whether to append the logs to existing ones
 * @param {number} [params.startTimestamp] - Timestamp for continuing from a previous page
 * @returns {Function} - Thunk action creator
 */
export const fetchGuestLogs = (params) => {
  return async (dispatch) => {
    const {guestId, append = false, startTimestamp} = params;

    dispatch(setApiStart("guestLogs"));
    dispatch(setGuestLogsLoading(true));

    try {
      const apiParams = {
        guest_id: guestId,
        size: 50,
      };

      if (startTimestamp) {
        apiParams.start_timestamp = startTimestamp;
      }

      const response = await guestLogsAPI.fetchGuestLogs(
        qs.stringify(apiParams),
      );

      const {items: logs, has_more: hasMore, last_item: lastItem} = response;

      if (append) {
        dispatch(addGuestLogs(logs, hasMore, lastItem));
      } else {
        dispatch(setGuestLogs(logs, hasMore, lastItem));
      }

      dispatch(setApiSuccess("guestLogs"));
      dispatch(setGuestLogsLoading(false));

      return {
        logs,
        hasMore,
        lastItem,
      };
    } catch (error) {
      console.error("Error fetching guest logs:", error);
      dispatch(setApiError("guestLogs", "Failed to fetch guest logs"));
      dispatch(setGuestLogsLoading(false));
      throw error;
    }
  };
};
